import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { BookModel } from '../../../interfaces/book.interface';
import { UserModel } from '../../../interfaces/user.interface';
import {
  AdminBooksTab,
  AdminDetailsHeader,
  AdminSummaryCard,
  AdminTabs,
  AdminTabDef,
  SummaryField,
} from '../../../components/Admin';
import Loader from '../../../components/Common/Loader';
import StudentsTable from '../Students/components/StudentsTable';
import { fetchStudents } from '../Students/services/Students.service';
import { convertTimestampToDateTime } from '../../../helper/helper';
import {
  ClassroomDetail,
  ClassroomProject,
  TemplateOption,
  fetchClassroomBookIds,
  getAdminClassroomDetail,
  getAdminProjectBooks,
  getTemplateOptions,
  updateAdminClassroom,
  updateAdminProject,
} from './services/Classrooms.service';
import { useBulkMarkPurchased, BulkPurchaseToolbar } from './components/BulkMarkPurchased';
import { AdminNavHandler } from '../adminNav';

type TabType = 'projects' | 'students' | 'books';

interface ClassroomDetailsViewProps {
  classroomId: string;
  onClose: () => void;
  onOptionClick: AdminNavHandler;
  onOpenProject: (projectId: string) => void;
}

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '—';
  const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ClassroomDetailsView: React.FC<ClassroomDetailsViewProps> = ({
  classroomId,
  onClose,
  onOptionClick,
  onOpenProject,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('projects');
  const [classroom, setClassroom] = useState<ClassroomDetail | null>(null);
  const [classroomLoader, setClassroomLoader] = useState(false);

  // Inline edits
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedTemplateId, setEditedTemplateId] = useState('');
  const [editedIsDefault, setEditedIsDefault] = useState(false);

  const [templates, setTemplates] = useState<TemplateOption[]>([]);

  // Students tab
  const [students, setStudents] = useState<UserModel[]>([]);
  const [studentsLoader, setStudentsLoader] = useState(false);

  // Books tab — uses the project endpoint with no projectId trick: instead we
  // fetch books across all projects in this classroom by paginating through
  // each. Simpler: rely on a single project's view — but admins want a
  // classroom-level view too. We'll union books from the project endpoints by
  // listing per-project. For v1 we show ALL classroom books via a flat call.
  const [books, setBooks] = useState<BookModel[]>([]);
  const [booksLoader, setBooksLoader] = useState(false);
  const [booksPage, setBooksPage] = useState(1);
  const [totalBooksPages, setTotalBooksPages] = useState(0);

  const fetchClassroom = async () => {
    setClassroomLoader(true);
    const detail = await getAdminClassroomDetail(classroomId);
    setClassroom(detail);
    setClassroomLoader(false);
    setEditingName(false);
    setEditingProjectId(null);
  };

  useEffect(() => {
    fetchClassroom();
  }, [classroomId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getTemplateOptions().then(setTemplates);
  }, []);

  // Students tab data
  const fetchClassroomStudents = async () => {
    setStudentsLoader(true);
    try {
      const result = await fetchStudents(1, { classroomId });
      setStudents(result.users);
    } finally {
      setStudentsLoader(false);
    }
  };

  // Books tab — concatenate books page-by-page across all projects.
  // For a typical classroom with a few projects this is cheap; cap at the
  // server's per-project limit.
  const fetchClassroomBooks = async (page: number) => {
    if (!classroom?.projects?.length) {
      setBooks([]);
      setTotalBooksPages(0);
      return;
    }
    setBooksLoader(true);
    try {
      const perProject = await Promise.all(
        classroom.projects.map((p) => getAdminProjectBooks(p.id, page)),
      );
      const merged: BookModel[] = perProject.flatMap((r) => r.books as unknown as BookModel[]);
      // Sort newest first
      merged.sort((a: any, b: any) => {
        const at = a?.timestamp?._seconds ? a.timestamp._seconds * 1000 : new Date(a?.timestamp ?? 0).getTime() || 0;
        const bt = b?.timestamp?._seconds ? b.timestamp._seconds * 1000 : new Date(b?.timestamp ?? 0).getTime() || 0;
        return bt - at;
      });
      setBooks(merged);
      // Best-effort: take the max totalPages across projects
      setTotalBooksPages(Math.max(0, ...perProject.map((r) => r.totalPages)));
    } finally {
      setBooksLoader(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'students' && students.length === 0) fetchClassroomStudents();
    if (activeTab === 'books' && classroom) fetchClassroomBooks(booksPage);
  }, [activeTab, classroom?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'books' && classroom) fetchClassroomBooks(booksPage);
  }, [booksPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    fetchClassroom();
    if (activeTab === 'students') {
      setStudents([]);
      fetchClassroomStudents();
    } else if (activeTab === 'books') {
      setBooks([]);
      fetchClassroomBooks(booksPage);
    }
  };

  // ── Bulk "mark as purchased" — shared selection/bulk logic ────────────────
  const bulk = useBulkMarkPurchased({
    classroomId,
    teacherEmail: classroom?.teacherEmail,
    onCompleted: () => fetchClassroomBooks(booksPage), // refresh purchased badges
    // Whole classroom (across all projects/pages), excluding already-purchased books.
    getSelectAllIds: async () => {
      const rows = await fetchClassroomBookIds(classroomId);
      return rows.filter((b) => b.bookId && (b.purchasedCounterValue || 0) <= 0).map((b) => b.bookId);
    },
  });

  // ── Inline edit: classroom name ──────────────────────────────────────────
  const startEditName = () => {
    if (!classroom) return;
    setEditedName(classroom.name);
    setEditingName(true);
  };

  const saveName = async () => {
    if (!classroom || !editedName.trim()) return;
    await updateAdminClassroom(classroom.id, { name: editedName.trim() });
    setEditingName(false);
    fetchClassroom();
  };

  // ── Inline edit: project ─────────────────────────────────────────────────
  const startEditProject = (project: ClassroomProject) => {
    setEditingProjectId(project.id);
    if (project.startDate) {
      const d = project.startDate._seconds
        ? new Date(project.startDate._seconds * 1000)
        : new Date(project.startDate);
      setEditedStartDate(!isNaN(d.getTime()) ? d.toISOString().split('T')[0] : project.startDateTime || '');
    } else {
      setEditedStartDate(project.startDateTime || '');
    }
    setEditedTemplateId(project.projectTemplateId || '');
    setEditedIsDefault(!!project.isDefaultProject);
  };

  const cancelEditProject = () => setEditingProjectId(null);

  const saveProject = async (projectId: string) => {
    const updates: { startDate?: string; startDateTime?: string; projectTemplateId?: string; isDefaultProject?: boolean } = {};
    if (editedStartDate) {
      updates.startDate = editedStartDate;
      updates.startDateTime = editedStartDate;
    }
    updates.projectTemplateId = editedTemplateId || '';
    updates.isDefaultProject = editedIsDefault;
    await updateAdminProject(projectId, updates);
    setEditingProjectId(null);
    fetchClassroom();
  };

  if (classroomLoader && !classroom) {
    return (
      <div className="max-w-5xl mx-auto min-h-screen flex justify-center items-center">
        <Loader loader={true} dimentions={'w-8 h-8'} />
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className="max-w-5xl mx-auto min-h-screen">
        <AdminDetailsHeader title="Classroom not found" onClose={onClose} />
      </div>
    );
  }

  const tabDef: AdminTabDef<TabType>[] = [
    { key: 'projects', label: `Projects (${classroom.projects?.length ?? 0})` },
    { key: 'students', label: `Students (${classroom.studentCount ?? 0})` },
    { key: 'books', label: `Books (${classroom.bookCount ?? 0})` },
  ];

  return (
    <div className="max-w-5xl mx-auto min-h-screen">
      <AdminDetailsHeader
        title={
          <>
            Classroom Details — {editingName ? (
              <span className="inline-flex items-center gap-2 align-middle">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-base font-normal text-gray-800"
                  autoFocus
                />
                <FontAwesomeIcon
                  icon={faSave}
                  className="cursor-pointer text-green-600 hover:text-green-800 text-base"
                  title="Save"
                  onClick={saveName}
                />
                <FontAwesomeIcon
                  icon={faTimes}
                  className="cursor-pointer text-gray-500 hover:text-red-600 text-base"
                  title="Cancel"
                  onClick={() => setEditingName(false)}
                />
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                {classroom.name}
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  className="cursor-pointer text-gray-400 hover:text-blue-600 text-sm"
                  title="Edit name"
                  onClick={startEditName}
                />
              </span>
            )}
          </>
        }
        onClose={onClose}
        onRefresh={handleRefresh}
        refreshing={classroomLoader || studentsLoader || booksLoader}
      />

      <AdminSummaryCard>
        <SummaryField label="Name" value={classroom.name} />
        <SummaryField
          label="Teacher"
          value={
            <button
              type="button"
              className="text-blue-600 hover:underline text-left"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                onOptionClick('Customers', { teacherEmail: classroom.teacherEmail });
              }}
            >
              {classroom.teacherEmail}
            </button>
          }
        />
        <SummaryField label="Group Code" value={<span className="font-mono">{classroom.groupCode || '—'}</span>} />
        <SummaryField label="Students" value={classroom.studentCount} />
        <SummaryField label="Projects" value={classroom.projects?.length ?? 0} />
        <SummaryField label="Books" value={classroom.bookCount ?? 0} />
        <SummaryField label="Created" value={classroom.createdAt ? convertTimestampToDateTime(classroom.createdAt) : '—'} />
        {classroom.description && (
          <SummaryField label="Description" value={classroom.description} />
        )}
      </AdminSummaryCard>

      <AdminTabs<TabType> tabs={tabDef} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'projects' && (
        <div>
          {classroom.projects && classroom.projects.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left py-2 px-3 border-b">Name</th>
                    <th className="text-left py-2 px-3 border-b">Status</th>
                    <th className="text-left py-2 px-3 border-b" title="Students assigned to this project">Students</th>
                    <th className="text-left py-2 px-3 border-b" title="Total books in this project">Books</th>
                    <th className="text-left py-2 px-3 border-b" title="Parent flyer responses">Flyer</th>
                    <th className="text-left py-2 px-3 border-b" title="Students with at least one order">Ordered</th>
                    <th className="text-left py-2 px-3 border-b">Start Date</th>
                    <th className="text-left py-2 px-3 border-b">Project</th>
                    <th className="text-left py-2 px-3 border-b">Default</th>
                    <th className="text-left py-2 px-3 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classroom.projects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="py-2 px-3 border-b font-medium">
                        <button
                          type="button"
                          className="text-storygreen underline hover:text-storyblue"
                          title="Open project details"
                          onClick={(e) => { e.stopPropagation(); onOpenProject(project.id); }}
                        >
                          {project.name}
                        </button>
                      </td>
                      <td className="py-2 px-3 border-b capitalize">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            project.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : project.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {(project.status || 'draft').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-2 px-3 border-b">{project.studentCount}</td>
                      <td className="py-2 px-3 border-b">
                        <button
                          type="button"
                          className="text-blue-600 hover:underline font-medium"
                          title="Open books grid"
                          onClick={(e) => { e.stopPropagation(); onOpenProject(project.id); }}
                        >
                          {project.bookCount ?? 0}
                        </button>
                      </td>
                      <td className="py-2 px-3 border-b">{project.parentResponseCount ?? 0}</td>
                      <td className="py-2 px-3 border-b">{project.orderedStudentCount ?? 0}</td>

                      {/* Start Date — editable */}
                      <td className="py-2 px-3 border-b">
                        {editingProjectId === project.id ? (
                          <input
                            type="date"
                            value={editedStartDate}
                            onChange={(e) => setEditedStartDate(e.target.value)}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs w-32"
                          />
                        ) : (
                          <span className="text-xs">
                            {project.startDateTime || formatDate(project.startDate)}
                          </span>
                        )}
                      </td>

                      {/* Project (template) — editable */}
                      <td className="py-2 px-3 border-b">
                        {editingProjectId === project.id ? (
                          <select
                            value={editedTemplateId}
                            onChange={(e) => setEditedTemplateId(e.target.value)}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs w-40"
                          >
                            <option value="">None</option>
                            {templates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-600">{project.templateName || '—'}</span>
                        )}
                      </td>

                      <td className="py-2 px-3 border-b text-center">
                        {editingProjectId === project.id ? (
                          <label className="flex items-center justify-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editedIsDefault}
                              onChange={(e) => setEditedIsDefault(e.target.checked)}
                              className="accent-green-600"
                            />
                            <span className="text-xs">{editedIsDefault ? 'Yes' : 'No'}</span>
                          </label>
                        ) : project.isDefaultProject ? (
                          <span className="text-green-600 font-bold text-xs">Yes</span>
                        ) : (
                          <span className="text-gray-400 text-xs">No</span>
                        )}
                      </td>

                      <td className="py-2 px-3 border-b">
                        {editingProjectId === project.id ? (
                          <div className="flex gap-2">
                            <FontAwesomeIcon
                              icon={faSave}
                              className="cursor-pointer text-green-600 hover:text-green-800"
                              title="Save"
                              onClick={() => saveProject(project.id)}
                            />
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="cursor-pointer text-gray-500 hover:text-red-600"
                              title="Cancel"
                              onClick={cancelEditProject}
                            />
                          </div>
                        ) : (
                          <FontAwesomeIcon
                            icon={faPenToSquare}
                            className="cursor-pointer text-gray-500 hover:text-blue-600"
                            title="Edit start date & project"
                            onClick={() => startEditProject(project)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No projects found.</div>
          )}
        </div>
      )}

      {activeTab === 'students' && (
        <div>
          {studentsLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={studentsLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : students.length > 0 ? (
            <StudentsTable
              students={students}
              onEditCredits={() => {}}
              onTeacherClick={(teacherEmail) => {
                onClose();
                onOptionClick('Customers', { teacherEmail });
              }}
            />
          ) : (
            <div className="text-center text-gray-500 py-8">No students found in this classroom.</div>
          )}
        </div>
      )}

      {activeTab === 'books' && (
        <div>
          <BulkPurchaseToolbar bulk={bulk} selectAllLabel="Select all (classroom)" />

          {booksLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={booksLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : (
            <AdminBooksTab
              books={books}
              currentPage={booksPage}
              totalBooksPages={totalBooksPages}
              handleBooksPageChange={(p) => setBooksPage(+p)}
              selectable={bulk.selectMode}
              selectedBookIds={bulk.selectedIds}
              onToggleSelect={bulk.toggleSelect}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ClassroomDetailsView;
